import { Button } from '@civilgrid/ui/components';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { updateRequestAssignmentsByProjectId as apiUpdateRequestAssignmentsByProjectId } from '~/api/civilsearch';
import { civilSearchTierLabels } from '~/common/dataRequestUtils';
import { CivilSearchTier, type Project } from '~/common/types';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import AdminSelect from './AdminSelect';
import RequestsByTierTable from './RequestsByTierTable';

const tierItems = Object.values(CivilSearchTier).map((tier) => ({
  label: civilSearchTierLabels[tier],
  value: tier,
}));

interface PFormValues {
  assignedTo: string;
  civilSearchTier?: CivilSearchTier;
}

interface ProjectFormProps {
  project: Project;
  assignedUsersText: string;
  onSuccess?: () => void;
}

export default function ProjectForm({ project, assignedUsersText, onSuccess }: ProjectFormProps) {
  const form = useForm<PFormValues>({
    defaultValues: {
      assignedTo: '',
    },
  });

  const onSubmit: SubmitHandler<PFormValues> = async (data) => {
    if (!form.formState.isDirty) return;
    try {
      const response = await apiUpdateRequestAssignmentsByProjectId(project.id, data.assignedTo, data.civilSearchTier);
      Notify.info({
        title: 'CivilSearch Assignments Updated',
        message: `CivilSearch Assignments successfully updated to ${response.assignedTo.name}.`,
      });
      form.reset();
      onSuccess?.();
    } catch {
      Notify.error({
        message: 'An error occurred while updating the assigned admin',
      });
    }
  };

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">Project ID</div>
          <div className="text-sm">{project.projectId}</div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">Project Name</div>
          <div className="text-sm">{project.name}</div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">Requests</div>
          <RequestsByTierTable requests={project.metadata['dataRequestsByTier']} />
          <div className="flex flex-col gap-2">
            <div className="text-sm">{assignedUsersText}</div>
            <AdminSelect form={form} projectId={project.projectId} />
            <ControlledSelect
              control={form.control}
              name="civilSearchTier"
              label="Assign Requests for Specific Tier"
              items={tierItems}
              isClearable
            />
            <Button type="submit" disabled={!form.formState.isDirty}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
}
